import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Label, BigLabel } from '../SubComponents/Text';
import { formatDuration } from '../Utils/utils';
import useFormatPrice from '../Hooks/use-format-price';
import { colors } from '../Style/Theme';
import useLocale from '../Hooks/use-locale';
import localizations from '../localizations';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DurationColumn = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: ${colors.lightBlue};
  padding: 0 5px;
`;

const DurationNumber = styled(BigLabel)<{ selected: boolean; disabled: boolean }>`
  color: ${(props) => (props.selected ? '#fff' : props.theme.textColor)};
  background-color: ${(props) =>
    props.selected ? props.theme.colors.primary : 'transparent'};
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  margin: 7.5px 0;
  border-radius: 100px;
  padding: 0 10px;
  white-space: nowrap;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.selected ? 1 : props.disabled ? 0.35 : 1)};

  :hover {
    color: ${(props) =>
      props.selected
        ? '#fff'
        : props.disabled
        ? props.theme.textColor
        : props.theme.highLightColor};
  }
`;

const DurationLabel = styled(Label)`
  white-space: nowrap;
  padding: 8px 6px;
`;

interface GetDisplayPricesProps {
  option: { priceLow?: number; priceHigh?: number };
  boatCount: number;
  formatPrice: (price: number) => string;
}

const getDisplayPrices = ({ option, boatCount, formatPrice }: GetDisplayPricesProps) => {
  if (!option.priceLow || !option.priceHigh) return '';

  let low = option.priceLow;
  let high = option.priceHigh;

  let formattedLow = undefined;
  let formattedHigh = undefined;

  if (!Number.isNaN(Number(low)) || !Number.isNaN(Number(high))) {
    formattedLow = formatPrice(low * boatCount);
    formattedHigh = formatPrice(high * boatCount);
  }

  if (formattedLow || formattedHigh) {
    if (!formattedLow || !formattedHigh) return '-';
    if (formattedLow === formattedHigh) return formattedLow;
    return `${formattedLow} - ${formattedHigh}`;
  }

  if (!low || !high) return '-';
  if (low === high) return low;
  return `${low} - ${high}`;
};

interface DurationOptionProps {
  option: { durationNumber: number; priceLow?: number; priceHigh?: number };
  selected: boolean;
  onChange: (duration: number) => void;
  enabled: boolean;
  boatCount: number;
}

const DurationOption = (props: DurationOptionProps) => {
  const { option, selected, onChange, enabled, boatCount } = props;
  const { languageAsEnum } = useLocale();
  const localization = localizations[languageAsEnum];

  const formatPrice = useFormatPrice();
  const formattedPrice = useMemo(
    () => getDisplayPrices({ option, boatCount, formatPrice }),
    [option, boatCount, formatPrice]
  );

  // Make the duration selectable with the Space and Enter key
  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLLabelElement>,
    newDuration: number
  ) => {
    if (event.key === ' ' || event.key === 'Enter') onChange(newDuration);
  };

  const numberProps = {
    selected,
    onClick: () => enabled && onChange(option.durationNumber),
    onKeyPress: (event: React.KeyboardEvent<HTMLLabelElement>) =>
      enabled && handleKeyPress(event, option.durationNumber),
    tabIndex: !enabled ? -1 : 0,
    disabled: !enabled,
  };

  const labelProps = {
    onClick: () => enabled && onChange(option.durationNumber),
    fontSize: '1.2rem',
    textAlign: 'center',
    width: '100%',
    monospaced: true,
  };

  return (
    <Container>
      <DurationColumn>
        <DurationNumber {...numberProps}>
          {formatDuration(option.durationNumber, { localization })}
        </DurationNumber>
      </DurationColumn>

      {/* @ts-expect-error */}
      <DurationLabel {...labelProps}>{formattedPrice}</DurationLabel>
    </Container>
  );
};

export default DurationOption;
